import { pickBy } from 'lodash';
import { atom } from 'recoil';
import { CacheKeys } from 'src/constants';
import { localStorageEffect } from 'src/lib/recoil';

export type MfaLoginResult = {
  status: 'success' | 'error';
  /** HTTP status of the failed call. Absent on success and when the request never got a response. */
  httpStatus?: number;
  /** ISO timestamp, so it can go straight into `formatDate`. */
  finishedAt: string;
};

/**
 * The last terminal outcome of an MFA login, keyed by credential id.
 *
 * Persisted because the user is asked to keep the result until they start the next attempt
 * (IRIS-6575), which outlives both navigation and a page reload.
 * In-flight state comes from the react-query MutationCache instead -- see `useMfaLogin`.
 */
export const mfaLoginResultsState = atom<Record<string, MfaLoginResult>>({
  key: 'mfaLogin/results',
  default: {},
  effects: [localStorageEffect(CacheKeys.mfaLoginResults)],
});

/**
 * Drops the failures and keeps the successes, for use when the session ends.
 *
 * A success reads as dated history ("Zuletzt angemeldet: ..."), which stays true for whoever looks
 * at it next. A failure claims something is wrong *now*, so leaving it for the next person to sign
 * in on the same machine would be a lie.
 *
 * Returns the input untouched when there is nothing to drop, so a signed-out tab does not rewrite
 * localStorage on every render.
 */
export const withoutErrors = (results: Record<string, MfaLoginResult>) => {
  const successes = pickBy(results, ({ status }) => status === 'success');

  return Object.keys(successes).length === Object.keys(results).length ? results : successes;
};
